import { useState } from "react";
import { KeyRound, Plus, Trash2 } from "lucide-react";
import { useCreateToken, useRevokeToken, useTokens } from "@/api/hooks";
import { errorMessage } from "@/api/client";
import type { CreatedToken } from "@/api/types";
import { formatDateTime, timeAgo } from "@/lib/utils";
import { useToast } from "@/lib/toast";
import { PageHeader, EmptyState, ErrorState } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CopyButton } from "@/components/ui/copy-button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageSkeleton } from "@/components/ui/skeleton";
import { SettingsNav } from "./SettingsConnect";

export function Snippet({ code, label }: { code: string; label?: string }) {
  return (
    <div className="rounded-lg border bg-muted/40">
      {label && <div className="border-b px-3 py-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</div>}
      <div className="flex items-start gap-2 p-3">
        <pre className="min-w-0 flex-1 overflow-x-auto whitespace-pre-wrap break-all font-mono text-xs leading-relaxed">{code}</pre>
        <CopyButton text={code} label="Copy" />
      </div>
    </div>
  );
}

export default function TokensPage() {
  const tokens = useTokens();
  const create = useCreateToken();
  const revoke = useRevokeToken();
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [created, setCreated] = useState<CreatedToken | null>(null);
  const [toRevoke, setToRevoke] = useState<{ id: string; name: string } | null>(null);
  const origin = window.location.origin;
  const mcpUrl = `${origin}/api/mcp`;

  const plaintext = created?.plaintext ?? "<token>";
  const claudeSnippet = `claude mcp add --transport http minne ${mcpUrl} --header "Authorization: Bearer ${plaintext}"`;
  const curlSnippet = `curl -H "Authorization: Bearer ${plaintext}" ${origin}/api/auth/me`;

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title="API tokens" description="Personal tokens for clients that cannot do OAuth, such as Claude Code with a static header." />
      <SettingsNav />

      <form
        className="mb-8 flex flex-col gap-2 sm:flex-row sm:items-end"
        onSubmit={(e) => {
          e.preventDefault();
          const n = name.trim();
          if (!n) return;
          create.mutate(n, {
            onSuccess: (t) => {
              setCreated(t);
              setName("");
            },
            onError: (err) => toast({ title: "Could not create token", description: errorMessage(err), tone: "error" }),
          });
        }}
      >
        <div className="grid flex-1 gap-1.5">
          <Label htmlFor="tok-name">Token name</Label>
          <Input id="tok-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="laptop, claude-code, …" />
          <p className="text-xs text-muted-foreground">The name is recorded as the client on notes it writes.</p>
        </div>
        <Button type="submit" disabled={!name.trim() || create.isPending} className="sm:mb-5">
          <Plus /> {create.isPending ? "Creating…" : "Create token"}
        </Button>
      </form>

      {tokens.isLoading && <PageSkeleton />}
      {tokens.isError && <ErrorState error={tokens.error} retry={() => tokens.refetch()} />}
      {tokens.data && tokens.data.length === 0 && <EmptyState icon={KeyRound} title="No tokens yet" description="Create one above to connect a client with a bearer header." />}
      {tokens.data && tokens.data.length > 0 && (
        <ul className="divide-y rounded-xl border bg-card">
          {tokens.data.map((t) => {
            const revoked = !!t.revokedAt;
            const expired = t.expiresAt ? new Date(t.expiresAt) < new Date() : false;
            return (
              <li key={t.id} className="flex items-center gap-3 px-4 py-3">
                <KeyRound className="size-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{t.name}</span>
                    <Badge variant={t.kind === "oauth" ? "info" : "secondary"}>{t.kind === "oauth" ? `OAuth · ${t.clientName ?? t.clientId ?? "client"}` : "PAT"}</Badge>
                    {revoked && <Badge variant="destructive">revoked</Badge>}
                    {expired && !revoked && <Badge variant="warning">expired</Badge>}
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    created {timeAgo(t.createdAt)}
                    {t.lastUsedAt ? ` · last used ${timeAgo(t.lastUsedAt)}` : " · never used"}
                    {t.expiresAt ? ` · expires ${formatDateTime(t.expiresAt)}` : ""}
                  </div>
                </div>
                {!revoked && (
                  <Button variant="ghost" size="icon-sm" aria-label={`Revoke ${t.name}`} title="Revoke" onClick={() => setToRevoke({ id: t.id, name: t.name })}>
                    <Trash2 className="text-destructive" />
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <section className="mt-10">
        <h2 className="mb-2 text-sm font-semibold">Using a token</h2>
        <div className="grid gap-3">
          <Snippet label="Claude Code" code={claudeSnippet} />
          <Snippet label="curl" code={curlSnippet} />
        </div>
      </section>

      <Dialog open={!!created} onOpenChange={(o) => !o && setCreated(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Token “{created?.token.name}” created</DialogTitle>
            <DialogDescription>Copy it now. It is shown only once; Minne keeps just a hash.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 rounded-lg border bg-muted/40 p-3">
            <code className="min-w-0 flex-1 break-all font-mono text-xs">{created?.plaintext}</code>
            {created && <CopyButton text={created.plaintext} label="Copy token" showLabel size="sm" variant="outline" />}
          </div>
          <div className="grid gap-3">
            <Snippet label="Claude Code" code={claudeSnippet} />
            <Snippet label="curl" code={curlSnippet} />
          </div>
          <DialogFooter>
            <Button onClick={() => setCreated(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!toRevoke} onOpenChange={(o) => !o && setToRevoke(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke “{toRevoke?.name}”?</DialogTitle>
            <DialogDescription>Clients using it stop working immediately.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setToRevoke(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={revoke.isPending}
              onClick={() =>
                toRevoke &&
                revoke.mutate(toRevoke.id, {
                  onSuccess: () => setToRevoke(null),
                  onError: (err) => toast({ title: "Could not revoke", description: errorMessage(err), tone: "error" }),
                })
              }
            >
              {revoke.isPending ? "Revoking…" : "Revoke"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
